import { addHours, now, isAfter } from 'owa-datetime';
import { getGuid } from 'owa-guid';
import { createSubtodo, Subtodo } from 'owa-todo-types';
import * as trace from 'owa-trace';
import { getTodoCache } from '../todoCache';
import handleCreateSubtodos from './handleCreateSubtodos';
import type { CustomData } from 'owa-analytics-types';

export interface CreateSubtodoResponse {
    items: Subtodo[];
    success: boolean;
}

export async function createSubtodos(
    partialSubtodos: Partial<Subtodo>[],
    parentTodoId: string,
    telemetryData: CustomData
): Promise<CreateSubtodoResponse> {
    const todoItem = getTodoCache().selectItemById(parentTodoId);
    if (!partialSubtodos || partialSubtodos.length <= 0 || !todoItem) {
        return {
            items: [],
            success: true,
        };
    }

    const oldSubtodos = todoItem.Subtasks;
    const insertionIndex = oldSubtodos?.length ?? 0;
    const numCreated = partialSubtodos.length;

    // By default, make new subtasks have the oldest orderDateTime
    const oldestSubtodo =
        oldSubtodos != undefined && oldSubtodos.length > 1
            ? oldSubtodos.reduce((oldest, subtodo) =>
                  isAfter(oldest.OrderDateTime, subtodo.OrderDateTime) ? subtodo : oldest
              )
            : null;
    const minOrderDate = oldestSubtodo == null ? now() : addHours(oldestSubtodo.OrderDateTime, -1);
    const fullSubtodos: Subtodo[] = partialSubtodos.map((subtodo, index) => {
        const minTime = addHours(minOrderDate, -index);
        return createSubtodo({
            Id: getGuid(),
            OrderDateTime: minTime,
            ...subtodo,
        });
    });

    const allSubtodos = oldSubtodos ? [...oldSubtodos, ...fullSubtodos] : fullSubtodos;

    // add local lies
    getTodoCache().updateItem(parentTodoId, { Subtasks: allSubtodos });

    // kick off subtodo creation
    const subtodos = await handleCreateSubtodos(fullSubtodos, parentTodoId, telemetryData);
    const subtodosSuccessfullyCreated = subtodos.filter(subtodo => subtodo !== null) as Subtodo[];

    // remove local lies
    const oldSubtodosWithLocalLies = getTodoCache().selectItemById(parentTodoId)?.Subtasks;
    let subtodosToUpdate: (Subtodo | null)[] = [];
    if (oldSubtodosWithLocalLies) {
        // swap all local lies with created items
        subtodosToUpdate = [...oldSubtodosWithLocalLies];
        for (let i = 0; i < numCreated; ++i) {
            subtodosToUpdate[insertionIndex + i] = subtodos[i];
        }

        // filter out unsuccessfully created items & replace
        const updatedSubtodos = subtodosToUpdate.filter(subtodo => subtodo !== null) as Subtodo[];
        getTodoCache().updateItem(parentTodoId, { Subtasks: updatedSubtodos });
    } else {
        trace.errorThatWillCauseAlert(
            '[createSubtodos] Subtodo local lies were not properly placed inside cache.'
        );
    }

    return {
        items: subtodosSuccessfullyCreated,
        success: subtodosSuccessfullyCreated.length === subtodos.length,
    };
}
